import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"

/**
 * LoginErrorPage — displayed when an OAuth provider callback fails.
 *
 * Ported from v1's `src/components/pages/LoginError.tsx`.
 *
 * The server OAuth flow redirects here with `?provider=google` (or apple,
 * github, microsoft) when it cannot retrieve user information from the
 * provider.
 *
 * Rendered outside DatabaseProvider (like /logout) so Dexie Cloud auth
 * doesn't interfere.
 */
export function LoginErrorPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const provider = searchParams.get("provider")
  // The provider's OAuth error code: cancelled (Apple: `user_cancelled_authorize`), or the provider
  // refused the account (`access_denied`, also Google's answer to a cancel).
  const reason = searchParams.get("reason")
  const cancelled = reason === "access_denied" || reason === "user_cancelled_authorize"
  // Opened in the iOS/Android app's in-app browser: going on from here belongs in the app.
  const inNativeApp = searchParams.get("app") === "native"

  const capitalizedProvider = useMemo(() => {
    if (!provider) return ""
    return provider.charAt(0).toUpperCase() + provider.slice(1)
  }, [provider])

  const errorMessage = useMemo(() => {
    if (!capitalizedProvider) return ""
    if (cancelled) {
      return t(
        "Signing in with {{provider}} was cancelled or not allowed. Try again, or sign in with an email code instead.",
        { provider: capitalizedProvider },
      )
    }
    return t(
      "We were unable to retrieve your information from your {{provider}} account. Please try again or contact support if the issue persists.",
      { provider: capitalizedProvider },
    )
  }, [capitalizedProvider, cancelled, t])

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-8">
      {/* Big sad face — matches v1's 50vw icon at 5% opacity */}
      <div className="mb-6 select-none text-center text-[clamp(100px,50vw,300px)] leading-none text-foreground/5">
        😞
      </div>

      <h1 className="text-xl font-semibold">
        {capitalizedProvider
          ? t("Login with {{provider}} failed", {
              provider: capitalizedProvider,
            })
          : t("Login failed")}
      </h1>

      {errorMessage && (
        <p className="mt-3 max-w-sm text-center text-sm text-muted-foreground">
          {errorMessage}
        </p>
      )}

      {inNativeApp ? (
        <p className="mt-6 max-w-sm text-center text-sm text-muted-foreground">
          {t("Close this window and try again in the app.")}
        </p>
      ) : (
        <a href="/" className="mt-6 text-sm text-primary hover:underline">
          {t("Try again")}
        </a>
      )}
    </div>
  )
}
